import { Link } from "react-router-dom";
import {
  MapPin, Layers, HeartHandshake, Clock, UserCheck, Sparkles, ClipboardList, Smile, Scale,
  Phone, ShieldCheck, ArrowRight, ArrowUpRight, MessageCircle, BadgeCheck,
} from "lucide-react";
import { Hero } from "../components/Hero";
import { Marquee } from "../components/Marquee";
import { Reveal } from "../components/Reveal";
import { ServicesGrid } from "../components/ServicesGrid";
import { EnquiryForm } from "../components/EnquiryForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  BRAND, TRUST_ITEMS, PILLARS, STEPS, HOSPITALITY_POINTS, WHY_US, LOCATIONS,
  SHOWCASE, FAQS, IMAGES,
} from "../data/content";
import { usePageMeta } from "../hooks/use-page-meta";
import { track } from "../lib/tracking";

const TRUST_ICONS = { map: MapPin, layers: Layers, heart: HeartHandshake, clock: Clock, user: UserCheck };
const WHY_ICONS = {
  sparkles: Sparkles, clipboard: ClipboardList, smile: Smile, scale: Scale,
  user: UserCheck, map: MapPin, phone: Phone, shield: ShieldCheck,
};

const Chapter = ({ num, eyebrow, title, desc, center = false }) => (
  <Reveal className={center ? "text-center" : ""}>
    <p className="eyebrow" data-testid={`chapter-${num}-eyebrow`}>
      <span className="mr-3 font-serif italic text-steel/50">{num}</span>
      {eyebrow}
    </p>
    <h2 className={`text-balance mt-4 font-serif text-3xl font-medium leading-tight tracking-tight text-ivory sm:text-5xl ${center ? "mx-auto max-w-3xl" : "max-w-3xl"}`}>
      {title}
    </h2>
    {desc && <p className={`mt-5 text-base leading-relaxed text-steel sm:text-lg ${center ? "mx-auto max-w-2xl" : "max-w-2xl"}`}>{desc}</p>}
    <div className={`gold-hairline mt-8 w-40 ${center ? "mx-auto" : ""}`} aria-hidden="true" />
  </Reveal>
);

const TrustStrip = () => (
  <section data-testid="trust-strip" className="border-b border-white/5">
    <div className="container-x grid grid-cols-2 gap-y-8 py-12 sm:grid-cols-3 lg:grid-cols-5">
      {TRUST_ITEMS.map((t, i) => {
        const Icon = TRUST_ICONS[t.icon];
        return (
          <Reveal key={t.text} delay={i * 0.07} className="flex items-center gap-3">
            <Icon size={18} className="shrink-0 text-gold" />
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-ivory/70">{t.text}</span>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const Introduction = () => (
  <section data-testid="intro-section" className="container-x py-24 sm:py-32">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <Chapter num="01" eyebrow="Our Philosophy" title="The Art of Invisible Security" />
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-steel sm:text-xl">
            The finest event security does not disturb the experience — it protects it. Celebrity
            Bouncers combines disciplined manpower, careful planning and professional guest
            interaction to help events move smoothly from arrival to closure.
          </p>
          <p className="mt-8 font-serif text-2xl italic leading-snug text-gold/90 sm:text-3xl">
            “Quietly alert. Clearly organised. Always guest-conscious.”
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <Reveal delay={0.2} className="relative h-full">
          <div className="relative h-72 overflow-hidden sm:h-96 lg:h-full lg:min-h-[420px]">
            <img
              src={IMAGES.team}
              alt="A smartly groomed professional in a tailored black suit, representative of the Celebrity Bouncers team standard"
              className="h-full w-full object-cover transition-transform duration-[2.5s] ease-out hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 to-transparent" />
            <p className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.25em] text-ivory/60">
              Representative imagery
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Pillars = () => (
  <section data-testid="signature-pillars" className="relative overflow-hidden border-y border-gold/10 bg-navy/40 py-24 sm:py-32">
    <div className="container-x">
      <Chapter num="03" eyebrow="Our Signature" title="Security That Complements the Occasion" center />
      <div className="mt-16 grid gap-px bg-white/5 sm:grid-cols-3">
        {PILLARS.map((p, i) => (
          <Reveal key={p.num} delay={i * 0.12} className="bg-obsidian">
            <div className="group flex h-full flex-col p-8 transition-colors duration-500 hover:bg-navy/70 sm:p-10">
              <span className="font-display text-4xl text-gold/40 transition-colors duration-500 group-hover:text-gold">{p.num}</span>
              <h3 className="mt-6 font-serif text-xl font-medium uppercase tracking-[0.12em] text-ivory sm:text-2xl">
                {p.title}
              </h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-steel">{p.text}</p>
              <div className="gold-hairline mt-8 w-16 transition-all duration-700 group-hover:w-full" aria-hidden="true" />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Showcase = () => (
  <section data-testid="event-showcase" className="container-x py-24 sm:py-32">
    <div className="flex flex-wrap items-end justify-between gap-6">
      <Chapter num="04" eyebrow="Cinematic Event Showcase" title="Where Quiet Confidence Stands" />
      <Reveal delay={0.1}>
        <Link
          to="/portfolio"
          data-testid="showcase-view-portfolio-link"
          className="group flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.22em] text-gold"
        >
          View Full Portfolio
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
    <div className="mt-14 grid gap-5 md:grid-cols-3">
      {SHOWCASE.slice(0, 6).map((item, i) => (
        <Reveal key={item.id} delay={(i % 3) * 0.08} className={i === 0 ? "md:col-span-2 md:row-span-2" : ""}>
          <Link
            to="/portfolio"
            data-testid={`showcase-card-${item.id}`}
            onClick={() => track("portfolio_view", { item: item.id, placement: "home" })}
            className={`group relative block overflow-hidden ${i === 0 ? "h-80 md:h-full md:min-h-[560px]" : "h-64 md:h-[270px]"}`}
          >
            <img
              src={item.img}
              alt={item.alt}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[1.8s] ease-out group-hover:scale-[1.06]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-obsidian/20 to-transparent" />
            <span className="absolute left-4 top-4 border border-gold/40 bg-obsidian/60 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold backdrop-blur-sm">
              {item.category}
            </span>
            <div className="absolute inset-x-0 bottom-0 p-5">
              <h3 className="font-serif text-xl font-medium text-ivory sm:text-2xl">{item.title}</h3>
              <p className="mt-1 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-steel">
                <MapPin size={12} className="text-gold" /> {item.city}
              </p>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
    <Reveal className="mt-6">
      <p className="text-xs italic text-steel/60">
        Sample showcase imagery — representative only. Real client events are published solely with permission.
      </p>
    </Reveal>
  </section>
);

const Process = () => (
  <section data-testid="process-section" className="border-y border-gold/10 bg-navy/40 py-24 sm:py-32">
    <div className="container-x grid gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Chapter num="05" eyebrow="How We Work" title="A Calm, Considered Process" />
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-md text-base leading-relaxed text-steel">
            Every engagement follows the same disciplined arc — from the first private conversation
            to a composed, professional closure.
          </p>
          <Link
            to="/contact"
            data-testid="process-discuss-btn"
            className="mt-10 inline-block border border-gold/60 px-8 py-4 text-[12px] font-bold uppercase tracking-[0.22em] text-gold transition-all duration-300 hover:bg-gold hover:text-obsidian"
          >
            Discuss Your Event Privately
          </Link>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <ol className="relative space-y-10 border-l border-gold/20 pl-8">
          {STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.08}>
              <li className="relative">
                <span className="absolute -left-[41px] top-1 h-4 w-4 rotate-45 border border-gold bg-obsidian" aria-hidden="true" />
                <span className="font-display text-sm tracking-[0.3em] text-gold/60">{s.num}</span>
                <h3 className="mt-2 font-serif text-xl font-medium text-ivory sm:text-2xl">{s.title}</h3>
                <p className="mt-2 max-w-lg text-sm leading-relaxed text-steel">{s.text}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

const Hospitality = () => (
  <section data-testid="hospitality-section" className="container-x py-24 sm:py-32">
    <div className="grid items-center gap-14 lg:grid-cols-12">
      <div className="relative lg:col-span-5">
        <Reveal>
          <div className="relative h-80 overflow-hidden sm:h-[460px]">
            <img
              src={IMAGES.hospitality}
              alt="Courteous hospitality service at a premium venue, representative of guest-first event support"
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 to-transparent" />
          </div>
          <div className="absolute -bottom-8 -right-4 hidden w-48 overflow-hidden border-4 border-obsidian sm:block lg:-right-8">
            <img
              src={IMAGES.hospitalityTwo}
              alt="Luxury hotel corridor prepared for arriving guests"
              loading="lazy"
              className="h-40 w-full object-cover"
            />
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <Chapter num="06" eyebrow="The Hospitality Experience" title="Protection Without Disturbing the Celebration" />
        <div className="mt-10 grid gap-x-8 gap-y-4 sm:grid-cols-2">
          {HOSPITALITY_POINTS.map((p, i) => (
            <Reveal key={p} delay={i * 0.05} className="flex items-start gap-3">
              <BadgeCheck size={17} className="mt-0.5 shrink-0 text-gold" />
              <span className="text-sm leading-relaxed text-ivory/80">{p}</span>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section data-testid="why-us-section" className="border-y border-gold/10 bg-navy/40 py-24 sm:py-32">
    <div className="container-x">
      <Chapter num="07" eyebrow="Why Celebrity Bouncers" title="Chosen for Composure" center />
      <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {WHY_US.map((w, i) => {
          const Icon = WHY_ICONS[w.icon];
          return (
            <Reveal key={w.title} delay={(i % 4) * 0.07}>
              <div className="group h-full border border-white/[0.07] bg-obsidian/60 p-7 transition-all duration-500 hover:border-gold/40">
                <Icon size={22} className="text-gold transition-transform duration-500 group-hover:-translate-y-1" />
                <h3 className="mt-5 font-serif text-lg font-medium leading-snug text-ivory">{w.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-steel">{w.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const LocationsPreview = () => (
  <section data-testid="locations-preview" className="container-x py-24 sm:py-32">
    <Chapter num="08" eyebrow="Service Locations" title="Across Andhra Pradesh & Telangana" />
    <div className="mt-14 grid gap-px bg-white/5 sm:grid-cols-2 lg:grid-cols-3">
      {LOCATIONS.map((l, i) => (
        <Reveal key={l.city} delay={(i % 3) * 0.07} className="bg-obsidian">
          <Link
            to="/locations"
            data-testid={`location-preview-${l.city.toLowerCase()}`}
            className="group flex h-full flex-col p-8 transition-colors duration-500 hover:bg-navy/70"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-2xl font-medium text-ivory transition-colors duration-300 group-hover:text-gold">
                {l.city}
              </h3>
              <ArrowUpRight size={18} className="text-gold/50 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-gold" />
            </div>
            <p className="mt-3 text-sm text-steel">{l.note}</p>
            {l.primary && (
              <span className="mt-4 w-fit border border-gold/30 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
                Head Office
              </span>
            )}
          </Link>
        </Reveal>
      ))}
    </div>
    <Reveal className="mt-8">
      <p className="text-sm text-steel">
        Other Andhra Pradesh and Telangana locations are served on request.{" "}
        <Link to="/contact" data-testid="locations-enquire-link" className="text-gold underline underline-offset-4">
          Ask about your city
        </Link>
        .
      </p>
    </Reveal>
  </section>
);

const Testimonials = () => (
  <section data-testid="testimonials-section" className="border-y border-gold/10 bg-navy/40 py-24 sm:py-28">
    <div className="container-x">
      <Chapter num="09" eyebrow="Client Words" title="Testimonials" center />
      <Reveal className="mx-auto mt-12 max-w-2xl">
        <div className="border border-dashed border-gold/30 bg-obsidian/60 p-10 text-center">
          <p className="font-serif text-xl italic leading-relaxed text-ivory/80">
            Verified client testimonials will appear here soon.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-steel">
            We publish only genuine reviews shared with permission — never invented names or ratings.
            Verified Google Business Profile reviews can also be connected here.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const Enquiry = () => (
  <section data-testid="enquiry-section" className="container-x py-24 sm:py-32">
    <div className="grid gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Chapter num="10" eyebrow="Private Enquiry" title="Tell Us About Your Event" />
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-md text-base leading-relaxed text-steel">
            Share a few details and our coordination team will respond with a considered,
            customised plan. Every enquiry is handled in confidence.
          </p>
          <div className="mt-10 space-y-4">
            <a
              href={BRAND.phonePrimaryHref}
              data-testid="enquiry-call-link"
              onClick={() => track("call_click", { placement: "enquiry_section" })}
              className="flex items-center gap-4 border border-white/10 p-4 transition-colors duration-300 hover:border-gold/40"
            >
              <Phone size={18} className="text-gold" />
              <div>
                <p className="text-sm font-semibold text-ivory">{BRAND.phonePrimary}</p>
                <p className="text-xs text-steel">Call the coordination team</p>
              </div>
            </a>
            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enquiry-whatsapp-link"
              onClick={() => track("whatsapp_click", { placement: "enquiry_section" })}
              className="flex items-center gap-4 border border-white/10 p-4 transition-colors duration-300 hover:border-gold/40"
            >
              <MessageCircle size={18} className="text-gold" />
              <div>
                <p className="text-sm font-semibold text-ivory">WhatsApp Our Team</p>
                <p className="text-xs text-steel">Fastest response, any day</p>
              </div>
            </a>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <Reveal delay={0.15}>
          <EnquiryForm testidPrefix="home-enquiry" />
        </Reveal>
      </div>
    </div>
  </section>
);

const Faq = () => (
  <section data-testid="faq-section" className="border-t border-gold/10 bg-navy/40 py-24 sm:py-32">
    <div className="container-x grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <Chapter num="11" eyebrow="Questions" title="Frequently Asked" />
      </div>
      <div className="lg:col-span-8">
        <Accordion type="single" collapsible data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-white/10">
              <AccordionTrigger
                data-testid={`faq-question-${i}`}
                className="text-left font-serif text-lg font-medium text-ivory transition-colors hover:text-gold hover:no-underline sm:text-xl"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent data-testid={`faq-answer-${i}`} className="text-sm leading-relaxed text-steel sm:text-base">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

const FinalCta = () => (
  <section data-testid="final-cta-section" className="relative overflow-hidden py-28 sm:py-40">
    <img
      src={IMAGES.finalCta}
      alt="A well-organised luxury event entrance with a red carpet and stanchions"
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover"
    />
    <div className="absolute inset-0 bg-obsidian/80" />
    <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/50 to-transparent" />
    <div className="container-x relative z-10">
      <Reveal>
        <p className="eyebrow">Begin the Conversation</p>
        <h2 className="text-balance mt-5 max-w-3xl font-serif text-3xl font-medium leading-tight text-ivory sm:text-5xl lg:text-6xl">
          Your Guests Remember the Experience.{" "}
          <em className="font-display italic text-gold">We Help Protect It.</em>
        </h2>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-ivory/75 sm:text-lg">
          Speak with our team for a customised event security and hospitality plan.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <a
            href={BRAND.phonePrimaryHref}
            data-testid="finalcta-call-btn"
            onClick={() => track("call_click", { placement: "final_cta" })}
            className="bg-gold px-8 py-4 text-[13px] font-bold uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-gold-soft"
          >
            Call {BRAND.phonePrimary}
          </a>
          <a
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="finalcta-whatsapp-btn"
            onClick={() => track("whatsapp_click", { placement: "final_cta" })}
            className="border border-ivory/30 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            WhatsApp Now
          </a>
          <Link
            to="/contact"
            data-testid="finalcta-quote-btn"
            className="border border-gold/50 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-obsidian"
          >
            Request a Quote
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  usePageMeta(
    "Celebrity Bouncers | Premium Event Security AP & Telangana",
    "Premium bouncers, VIP movement support, access management and guest-focused event security for weddings, launches, concerts and corporate events."
  );
  return (
    <>
      <Hero />
      <Marquee />
      <TrustStrip />
      <Introduction />
      <section data-testid="services-section" className="container-x pb-24 sm:pb-32">
        <Chapter
          num="02"
          eyebrow="Premium Services"
          title="Composed Support for Every Occasion"
          desc="Ten specialised services, each planned around your venue, your guests and the tone of your event."
        />
        <div className="mt-14">
          <ServicesGrid />
        </div>
      </section>
      <Pillars />
      <Showcase />
      <Process />
      <Hospitality />
      <WhyUs />
      <LocationsPreview />
      <Testimonials />
      <Enquiry />
      <Faq />
      <FinalCta />
    </>
  );
}
